// hooks/usePasswordResetSubmit.js
import {useIntl} from 'react-intl'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {
    API_ERROR_MESSAGE,
    FEATURE_UNAVAILABLE_ERROR_MESSAGE
} from '@salesforce/retail-react-app/app/constants'

import {sendViewErrorEvent} from '../utils/gtm-utils'

/**
 * Custom hook for handling password reset submission logic
 *
 * @param {Object} form - React Hook Form instance
 * @param {Function} onSuccess - Callback function executed on successful password reset
 * @param {Function} onError - Callback function executed on error
 * @returns {Object} Hook methods
 */
const usePasswordResetSubmit = (form, onSuccess, onError) => {
    const {formatMessage} = useIntl()
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()

    /**
     * Process password reset via OTP service
     * @private
     */
    const processPasswordReset = async (email, brand = 'garage') => {
        const token = await getTokenWhenReady()

        const response = await fetch(`${appOrigin}/process-otp`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                token,
                email,
                brand
            })
        })

        if (!response.ok) {
            throw new Error('Failed to call OTP service')
        }

        const otpData = await response.json()

        if (!otpData?.success) {
            const errorMessage = otpData?.error_message || 'Failed to send OTP'
            throw new Error(errorMessage)
        }

        return otpData
    }

    /**
     * Main form submission handler
     */
    const submitForm = async (data) => {
        try {
            // Clear any existing errors
            form?.clearErrors()

            // Process password reset
            const result = await processPasswordReset(data.email)

            // Execute success callback
            if (onSuccess) {
                onSuccess(data, result)
            }

        } catch (error) {
            console.error('Password reset error:', error)

            // Determine error message based on error type
            let errorMessage
            if (error.message.includes('Failed to call OTP service')) {
                errorMessage = formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE)
            } else if (error.message.includes('Failed to send OTP')) {
                errorMessage = 'Failed to send password reset email. Please try again.'
            } else {
                errorMessage = formatMessage(API_ERROR_MESSAGE)
            }

            // Call error callback or set form error
            if (onError) {
                onError(errorMessage, error)
            } else if (form?.setError) {
                form.setError('global', {
                    type: 'manual',
                    message: errorMessage
                })
                // GTM Modification
                sendViewErrorEvent(errorMessage)
            }
        }
    }

    return {
        submitForm,
        isLoading: false // No mutation hook used, handle loading state manually if needed
    }
}

export {usePasswordResetSubmit}
